import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import SText from '../../../components/opinionless/SText'
import { useTranslation } from '../../../i18n'
import STheme, { SStyled, useSTheme } from '../../../styles/theme'
import { useAddApplicationAnswersMutation } from '../../../types/graphql'

import { Checkbox } from '@rebass/forms'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import SInputLabel from 'components/opinionless/SInputLabelV2'
import SRadioLabel from 'components/opinionless/SRadioLabelV2'
import SRadio from 'components/opinionless/SRadioV2'
import SFlex from '../../../components/opinionless/SFlex'

import STooltip from 'components/opinionless/STooltip'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

import { useRouter } from 'next/router'
import { ga4_useTitle } from 'utils/ga4'
import FButtonPrevious from '../../../components/BaseComponents/FButton/FPreviousButton'
import { ApplicationContext } from '../../../v2/context/application'
import { OrganizationContext } from '../../../v2/context/organization'
import { useApplicationAnswers } from '../../../v2/hooks/useApplicationAnswers'
import { useApplicationBasicInfo } from '../../../v2/hooks/useApplicationBasicInfo'
import { LBButton } from './ApplicationStepLBIntroduction'

const InputRow = SStyled(SFlex)`
    flex-direction: row;
    margin-top: 24px;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
            gap: 9px;
        }
    `}
`
export const SCheckBoxLabel = SStyled(SBox)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-bottom: 7px;
        }
    `}
`
export const SRadioLabelV2 = SStyled(SRadioLabel)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-bottom: 9px;
        }
    `}
`
export const CheckboxV2 = SStyled(Checkbox)`
    ${(p) => `
    @media screen and (max-width: ${p.theme.breakpoints[3]}) {
        margin-right: 15px;
    }
    `}
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-right: 7px;
            width: 45px;
            height: 45px;
        }
    `}
`
export const SRadioV2 = SStyled(SRadio)`
    ${(p) => `
    @media screen and (max-width: ${p.theme.breakpoints[3]}) {
        margin-right: 15px;
    }
    `}
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-right: 7px;
            gap: 0px;
            width: 33px;
            height: 33px;
        }
    `}
`

export const LBInputLabel = SStyled(SInputLabel)`
    margin-top: 24px;
    margin-bottom: 24px;
`

export const LBCheckboxContainer = SStyled(SBox)`
    margin-top: 15px;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-top: 9px;
        }
    `}
`
const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

const LBText = SStyled(SText)`
    margin-top: 24px;
    margin-bottom: 24px;

`

interface Props {
    previousStep?: () => any // Adjust the type to accept a number argument
    setCurrentStep?: (number) => void
    onSuccess?: () => void
    fontFamily: any
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const ApplicationStepLBRemainingQuestions: React.FC<Props> = (props: Props) => {
    const applicationContext = useContext(ApplicationContext)
    const organizationContext = useContext(OrganizationContext)
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { handleSubmit, errors, setError, clearErrors, register, setValue } = useForm()
    const [addApplicationAnswers, { loading }] = useAddApplicationAnswersMutation()
    const [checkedParentCaregiver, setCheckedParentCaregiver] = useState(false)
    const [checkedPregnant, setCheckedPregnant] = useState(false)
    const [noneOfTheAbove, setNoneOfTheAbove] = useState(false)
    const [isRequired, setIsRequired] = useState(true)
    const [hasAppliedToOtherProgramsRadioBtn, setHasAppliedToOtherProgramsRadioBtn] = useState(null)
    const router = useRouter()

    const { getAnswersByCategory, refetch } = useApplicationAnswers()
    useApplicationBasicInfo()

    ga4_useTitle('Remaining questions')

    const fontFamily = props.fontFamily

    useEffect(() => {
        if (applicationContext.application.id) {
            router.replace(
                `${organizationContext.program.urlFragment}/apply/${applicationContext.application.id}/basic`
            )
        }
    }, [])

    const handleNoneOfTheAbove = () => {
        if (!noneOfTheAbove) {
            clearErrors('parentCaregiverChildrenUnder18')
            setCheckedParentCaregiver(false)
            setCheckedPregnant(false)
        }
    }
    useEffect(() => {
        if (checkedParentCaregiver || checkedPregnant || noneOfTheAbove) {
            setIsRequired(false)
        }
    }, [])

    useEffect(() => {
        if (applicationContext.answers.length) {
            const householdResidencyResponse = getAnswersByCategory('HOUSEHOLD_RESIDENCY')?.[0]?.response
            const housingStatusResponse = getAnswersByCategory('HOUSING_STATUS')?.[0]?.response
            const pregnancyResponse = getAnswersByCategory('PREGNANCY')?.[0]?.response
            const hasAppliedToOtherProgramsResponse = getAnswersByCategory('HAS_APPLIED_TO_OTHER_PROGRAMS')?.[0]
                ?.response

            setValue('householdResidency', householdResidencyResponse)
            setValue('housingStatus', housingStatusResponse)
            setValue('parentCaregiverChildrenUnder18', pregnancyResponse)
            setValue('hasAppliedToOtherPrograms', hasAppliedToOtherProgramsResponse)

            if (pregnancyResponse?.includes('long_beach_pregnant')) {
                setCheckedPregnant(true)
            }

            if (pregnancyResponse?.includes('long_beach_parent_caregiver_of_one_or_more_children_under_18')) {
                setCheckedParentCaregiver(true)
            }

            if (pregnancyResponse === 'long_beach_none_of_the_above') {
                setNoneOfTheAbove(true)
            }

            setHasAppliedToOtherProgramsRadioBtn(hasAppliedToOtherProgramsResponse)
        }
    }, [applicationContext.answers])

    const parentCaregiverChildrenUnder18 = () => {
        clearErrors('parentCaregiverChildrenUnder18')
        setNoneOfTheAbove(false)
    }

    const handleHasApplied = (e) => {
        setValue('hasAppliedToOtherPrograms', e.target.getAttribute('value'))
        clearErrors('hasAppliedToOtherPrograms')
        clearErrors('continueButton')
        setHasAppliedToOtherProgramsRadioBtn(e.target.getAttribute('value'))
    }

    const onSubmit = (values) => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'PREGNANCY',
                        response: values.parentCaregiverChildrenUnder18.join(', '),
                    },
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'HAS_APPLIED_TO_OTHER_PROGRAMS',
                        response: values.hasAppliedToOtherPrograms,
                    },
                ],
            },
        }).then((response) => {
            if (response.data.addApplicationAnswers.success === true) {
                refetch()
                props.onSuccess()
            } else {
                setError('continueButton', { type: 'oops', message: t('error') })
            }
        })
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.basicInfo,
        WIZARD_STEPS_MAPPING.basicInfo.applicantRemainingInfo,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)
    return (
        <Container>
            <FButtonPrevious onClick={props.previousStep} />
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <LBText
                        dangerouslySetInnerHTML={{
                            __html: t('select_all_that_apply'),
                        }}
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            lineHeight: '140%',
                            fontWeight: '700',
                            fontSize: '14px',
                            opacity: '87%',
                            color: theme.colors.blackLabelfont,
                            fontFamily: fontFamily.body,
                        }}
                    ></LBText>
                    <STooltip content={t('select_all_that_apply')}></STooltip>
                </div>
                <SCheckBoxLabel
                    padding={'0 !important'}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: '11px',
                    }}
                    onClick={() => {
                        parentCaregiverChildrenUnder18(), setCheckedParentCaregiver(!checkedParentCaregiver)
                    }}
                    fontSize={'12px'}
                    width={'100%'}
                >
                    <CheckboxV2
                        defaultChecked={checkedParentCaregiver}
                        checked={checkedParentCaregiver}
                        id="parentCaregiverChildrenUnder18ParentCaregiver"
                        name="parentCaregiverChildrenUnder18"
                        value="long_beach_parent_caregiver_of_one_or_more_children_under_18"
                        ref={register({
                            required: isRequired && 'required_field',
                        })}
                        color={
                            checkedParentCaregiver
                                ? theme.colors.secondaryColor
                                : theme.colors.grayUnselectedCheckboxOutline
                        }
                        size={'36px'}
                    />
                    <SText
                        sx={{
                            width: '100%',
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            lineHeight: '140%',
                            fontWeight: '400',
                            fontSize: '14px',
                            color: theme.colors.gray400,
                            fontFamily: fontFamily.body,
                        }}
                    >
                        {t('long_beach_parent_caregiver_of_one_or_more_children_under_18')}
                    </SText>
                </SCheckBoxLabel>
                <SCheckBoxLabel
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: '11px',
                    }}
                    onClick={() => {
                        parentCaregiverChildrenUnder18(), setCheckedPregnant(!checkedPregnant)
                    }}
                    fontSize={'12px'}
                    width={'100%'}
                >
                    <CheckboxV2
                        defaultChecked={checkedPregnant}
                        checked={checkedPregnant}
                        id="parentCaregiverChildrenUnder18Pregnant"
                        name="parentCaregiverChildrenUnder18"
                        value="long_beach_pregnant_variation"
                        ref={register({
                            required: isRequired && 'required_field',
                        })}
                        color={
                            checkedPregnant ? theme.colors.secondaryColor : theme.colors.grayUnselectedCheckboxOutline
                        }
                        size={'36px'}
                    />
                    <SText
                        sx={{
                            width: '100%',
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            lineHeight: '140%',
                            fontWeight: '400',
                            fontSize: '14px',
                            color: theme.colors.gray400,
                            fontFamily: fontFamily.body,
                        }}
                    >
                        {t('long_beach_pregnant_variation')}
                    </SText>
                </SCheckBoxLabel>
                <SCheckBoxLabel
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: '11px',
                    }}
                    id={'last-selection-checkbox'}
                    onClick={() => {
                        handleNoneOfTheAbove(), setNoneOfTheAbove(!noneOfTheAbove)
                    }}
                    fontSize={'12px'}
                    width={'100%'}
                >
                    <CheckboxV2
                        defaultChecked={noneOfTheAbove}
                        checked={noneOfTheAbove}
                        id="parentCaregiverChildrenUnder18NoneOfTheAbove"
                        name="parentCaregiverChildrenUnder18"
                        value="long_beach_none_of_the_above"
                        ref={register({
                            required: isRequired && 'required_field',
                        })}
                        color={
                            noneOfTheAbove ? theme.colors.secondaryColor : theme.colors.grayUnselectedCheckboxOutline
                        }
                        size={'36px'}
                    />
                    <SText
                        sx={{
                            width: '100%',
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            lineHeight: '140%',
                            fontWeight: '400',
                            fontSize: '14px',
                            color: theme.colors.gray400,
                            fontFamily: fontFamily.body,
                        }}
                    >
                        {t('long_beach_none_of_the_above')}
                    </SText>
                </SCheckBoxLabel>
                <SInputError
                    forElementId={'last-selection-checkbox'}
                    sx={{
                        fontFamily: fontFamily.body,
                        color: theme.colors.redError,
                    }}
                >
                    {t(errors.parentCaregiverChildrenUnder18?.message)}
                </SInputError>

                <LBInputLabel
                    sx={{
                        color: STheme.colors.textColor,
                        fontFamily: fontFamily.body,
                        fontSize: '14px',
                        fontWeight: '700',
                    }}
                >
                    {t('has_applied_to_other_programs_question')}
                </LBInputLabel>
                <InputRow
                    id="has_applied_to_other_programs_question"
                    sx={{ display: 'flex !important', flexDirection: 'column !important' }}
                >
                    <SRadioLabelV2
                        color={theme.colors.textColor}
                        sx={{
                            fontFamily: fontFamily.body,
                        }}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="yes"
                                id="yes"
                                type="radio"
                                value="yes"
                                checked={hasAppliedToOtherProgramsRadioBtn === 'yes'}
                                color={
                                    hasAppliedToOtherProgramsRadioBtn === 'yes'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                onClick={handleHasApplied}
                                {...register('hasAppliedToOtherPrograms', {
                                    required: 'required_selection' as string,
                                })}
                                size={'26px'}
                            />
                        </SBox>

                        <SText
                            sx={{
                                width: '100%',
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                lineHeight: '140%',
                                fontWeight: '400',
                                fontSize: '14px',
                                color: theme.colors.gray400,
                                fontFamily: fontFamily.body,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('yes'),
                            }}
                        />
                    </SRadioLabelV2>
                    <SRadioLabelV2
                        color={theme.colors.textColor}
                        sx={{
                            fontFamily: fontFamily.body,
                        }}
                        marginBottom={'8px'}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="no"
                                id="no"
                                type="radio"
                                value="no"
                                checked={hasAppliedToOtherProgramsRadioBtn === 'no'}
                                color={
                                    hasAppliedToOtherProgramsRadioBtn === 'no'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                onClick={handleHasApplied}
                                {...register('hasAppliedToOtherPrograms', {
                                    required: 'required_selection' as string,
                                })}
                                size={'26px'}
                            />
                        </SBox>

                        <SText
                            sx={{
                                width: '100%',
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                lineHeight: '140%',
                                fontWeight: '400',
                                fontSize: '14px',
                                color: theme.colors.gray400,
                                fontFamily: fontFamily.body,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('no'),
                            }}
                        />
                    </SRadioLabelV2>
                </InputRow>

                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={loading}
                    title={t('save_and_continue')}
                    width={'100%'}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>

                <SInputError
                    forElementId={'has_applied_to_other_programs_question'}
                    sx={{
                        fontFamily: fontFamily.body,
                        color: theme.colors.redError,
                    }}
                >
                    {t(errors.hasAppliedToOtherPrograms?.message)}
                </SInputError>

                {errors.lbRemainingQuestions && (
                    <SInputError
                        forElementId={'continueButton'}
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                    >
                        {t(errors.lbRemainingQuestions?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default ApplicationStepLBRemainingQuestions
